import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Products.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const blankForm = (sub) => ({
  id: '', name: '', name_for_invoice: '', hsn_code: '', description: '',
  category_id: '', subcategory_id: '', prod_or_combo: 'P', status: 'A',
  veg_nv_e: 'V', freeze_definition: 'N', sgst: '2.5', cgst: '2.5',
  uom: 'Pack', subsidiary: sub, image_url: '', delivery_fee_exempt: 'N',
  is_kit: false, kit_item_days: '', kit_delivery_days: [], kit_consumption_days: [],
});

const blankSearch = {
  name: '', category_id: '', subcategory_id: '',
  status: '', prod_or_combo: 'P', freeze_definition: '',
};

const Products = () => {
  const { user, pageAccess } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const sub = localStorage.getItem('selectedSubsidiaryKey') || 'LH';
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchSubcats, setSearchSubcats] = useState([]);
  const [formSubcats, setFormSubcats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEditFrozen, setCanEditFrozen] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blankForm(sub));
  const [searchForm, setSearchForm] = useState(location.state?.searchForm || blankSearch);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kitItems, setKitItems] = useState([]); // [{ item_product_id, quantity, item_name }]
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    (async () => {
      if (user && pageAccess) {
        const ok = await pageAccess('freezeProductDefinition', user.role);
        setCanEditFrozen(ok);
      }
      fetchCategories();
    })();
  }, [user]);

  useEffect(() => { fetchProducts(); }, [searchForm]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/products/products`, {
        params: { ...searchForm, subsidiary: sub },
        ...auth(),
      });
      setProducts(res.data);
    } catch {
      setError('Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/productCategories`, auth());
      setCategories(res.data);
    } catch {}
  };

  const fetchSubcats = async (catId, forForm) => {
    if (!catId) { forForm ? setFormSubcats([]) : setSearchSubcats([]); return; }
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/productSubcategories/${catId}`, auth());
      forForm ? setFormSubcats(res.data) : setSearchSubcats(res.data);
    } catch {}
  };

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'category_id') fetchSubcats(value, true);
  };


  const openEdit = async (p) => {
    setForm({
      ...blankForm(sub), ...p,
      name_for_invoice: p.name_for_invoice ?? '',
      hsn_code: p.hsn_code ?? '',
      sgst: p.sgst ?? '2.5',
      cgst: p.cgst ?? '2.5',
      uom: p.uom ?? 'Pack',
      description: p.description ?? '',
      freeze_definition: p.freeze_definition === 'Y' ? 'Y' : 'N',
      subsidiary: p.subsidiary === 'ALL' ? 'ALL' : sub,
      image_url: p.image_url ?? '',
      delivery_fee_exempt: p.delivery_fee_exempt === 'Y' ? 'Y' : 'N',
      is_kit: !!p.is_kit,
      kit_item_days: p.kit_item_days ?? '',
      kit_delivery_days: p.kit_delivery_days || [],
      kit_consumption_days: p.kit_consumption_days || [],
    });
    fetchSubcats(p.category_id, true);
    setError('');
    // Load kit items and all products for the kit item dropdown
    if (p.id) {
      try {
        const [kitRes, allRes] = await Promise.all([
          axios.get(`${API_URL}/products/kit-items/${p.id}`, auth()),
          axios.get(`${API_URL}/products/products`, { params: { status: 'A', subsidiary: sub }, ...auth() }),
        ]);
        setKitItems(kitRes.data.map(r => ({ item_product_id: r.item_product_id, quantity: r.quantity, item_name: r.item_name })));
        setAllProducts(allRes.data);
      } catch {
        setKitItems([]);
      }
    } else {
      setKitItems([]);
    }
    setModal('edit');
  };

  const openAdd = () => {
    setForm(blankForm(sub));
    setFormSubcats([]);
    setKitItems([]);
    setError('');
    // Load all products for kit item dropdown
    axios.get(`${API_URL}/products/products`, { params: { status: 'A', subsidiary: sub }, ...auth() })
      .then(r => setAllProducts(r.data)).catch(() => {});
    setModal('add');
  };

  const closeModal = () => { setModal(null); setError(''); };

  const isServerImage = (url) =>
    url && url.startsWith('/uploads/product-images/');

  const deleteServerImage = async (url) => {
    if (!isServerImage(url)) return;
    try {
      await axios.delete(`${API_URL}/products/image`, {
        ...auth(), data: { url },
      });
    } catch {
      // non-fatal — log but don't block the user
      console.warn('Could not delete old product image from server:', url);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      // Delete the old image from the server before uploading the new one
      if (isServerImage(form.image_url)) {
        await deleteServerImage(form.image_url);
      }
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post(`${API_URL}/products/upload-image`, data, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setField('image_url', res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        sgst: form.sgst === '' ? null : Number(form.sgst),
        cgst: form.cgst === '' ? null : Number(form.cgst),
        kit_item_days: form.kit_item_days === '' ? null : Number(form.kit_item_days),
        kit_delivery_days: form.is_kit ? form.kit_delivery_days : null,
        kit_consumption_days: form.is_kit ? form.kit_consumption_days : null,
      };
      let savedId = form.id;
      if (modal === 'edit') {
        await axios.put(`${API_URL}/products/${form.id}`, payload, auth());
      } else {
        const res = await axios.post(`${API_URL}/products`, payload, auth());
        savedId = res.data.id;
      }
      // Save kit items if this is a kit product
      if (form.is_kit && savedId) {
        await axios.put(`${API_URL}/products/kit-items/${savedId}`, { items: kitItems }, auth());
      }
      setSuccess(modal === 'edit' ? 'Product updated.' : 'Product added.');
      closeModal();
      fetchProducts();
    } catch {
      setError('Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchForm(prev => ({ ...prev, [name]: value }));
    if (name === 'category_id') fetchSubcats(value, false);
  };

  const vegLabel = (v) => ({ V: 'Veg', N: 'Non-Veg', E: 'Egg' }[v] || v);

  return (
    <div className="pd-page">

      {/* Header */}
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-title">Products</h1>
          <p className="pd-page-sub">Manage product definitions and configurations</p>
        </div>
        <button className="btn-add-user" onClick={openAdd}>
          <span className="icon">+</span> Add Product
        </button>
      </div>

      {/* Alerts */}
      {error && !modal && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="pd-filters">
        <select name="category_id" value={searchForm.category_id} onChange={handleSearchChange} className="pd-filter-sel">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select name="subcategory_id" value={searchForm.subcategory_id} onChange={handleSearchChange} className="pd-filter-sel" disabled={!searchForm.category_id}>
          <option value="">All Subcategories</option>
          {searchSubcats.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="pd-filter-search">
          <span className="search-icon">🔍</span>
          <input name="name" value={searchForm.name} onChange={handleSearchChange} placeholder="Product name…" className="search-input" />
        </div>
        <select name="status" value={searchForm.status} onChange={handleSearchChange} className="pd-filter-sel">
          <option value="">All Status</option>
          <option value="A">Active</option>
          <option value="I">Inactive</option>
        </select>
        <select name="prod_or_combo" value={searchForm.prod_or_combo} onChange={handleSearchChange} className="pd-filter-sel">
          <option value="">All Types</option>
          <option value="P">Product</option>
          <option value="C">Combo</option>
        </select>
        <select name="freeze_definition" value={searchForm.freeze_definition} onChange={handleSearchChange} className="pd-filter-sel">
          <option value="">All Freeze</option>
          <option value="Y">Frozen</option>
          <option value="N">Not Frozen</option>
        </select>
        <button className="pd-btn-reset" onClick={() => setSearchForm(blankSearch)}>Reset</button>
      </div>

      {/* Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Invoice Name</th>
              <th>HSN</th>
              <th>Subcategory</th>
              <th>Type</th>
              <th>Status</th>
              <th>Veg / NV</th>
              <th>UOM</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="no-data">Loading…</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={9} className="no-data">No products found.</td></tr>
            ) : products.map(p => (
              <tr key={p.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">{p.name[0]?.toUpperCase()}</div>
                    <div>
                      <div>{p.name}</div>
                      {p.freeze_definition === 'Y' && <span className="pd-frozen-badge">Frozen</span>}
                    </div>
                  </div>
                </td>
                <td>{p.name_for_invoice || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>{p.hsn_code || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>{p.subcategory_name}</td>
                <td>{p.prod_or_combo === 'P' ? 'Product' : 'Combo'}</td>
                <td>
                  <span className={`status-badge ${p.status === 'A' ? 'status-active' : 'status-inactive'}`}>
                    {p.status === 'A' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{vegLabel(p.veg_nv_e)}</td>
                <td>{p.uom || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => openEdit(p)}
                      disabled={!canEditFrozen && p.freeze_definition === 'Y'}
                      title="Edit"
                    >✏️</button>
                    {p.prod_or_combo === 'C' ? (
                      <button
                        className="btn-edit"
                        onClick={() => navigate('/setup/product-combo', {
                          state: { product_id: p.id, freeze_definition: p.freeze_definition, searchForm }
                        })}
                        title="Child Products"
                      >🔗</button>
                    ) : (
                      <button
                        className="btn-edit"
                        onClick={() => navigate('/setup/prod-ingredients', {
                          state: { product_id: p.id, freeze_definition: p.freeze_definition, searchForm }
                        })}
                        title="Ingredients"
                      >🧪</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content pd-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal === 'edit' ? 'Edit Product' : 'Add Product'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {error && <div className="pd-form-error">{error}</div>}
              <div className="pd-form-grid3">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input required className="form-input" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Product Name" />
                </div>
                <div className="form-group">
                  <label>Invoice Name</label>
                  <input className="form-input" value={form.name_for_invoice} onChange={e => setField('name_for_invoice', e.target.value)} placeholder="Name for Invoice" />
                </div>
                <div className="form-group">
                  <label>HSN Code</label>
                  <input className="form-input" value={form.hsn_code} onChange={e => setField('hsn_code', e.target.value)} placeholder="HSN Code" />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select required className="form-input" value={form.category_id} onChange={e => setField('category_id', e.target.value)}>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategory *</label>
                  <select required className="form-input" value={form.subcategory_id} onChange={e => setField('subcategory_id', e.target.value)}>
                    <option value="">Select Subcategory</option>
                    {formSubcats.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select required className="form-input" value={form.prod_or_combo} onChange={e => setField('prod_or_combo', e.target.value)}>
                    <option value="P">Product</option>
                    <option value="C">Combo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select required className="form-input" value={form.status} onChange={e => setField('status', e.target.value)}>
                    <option value="A">Active</option>
                    <option value="I">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Veg / Non-Veg *</label>
                  <select required className="form-input" value={form.veg_nv_e} onChange={e => setField('veg_nv_e', e.target.value)}>
                    <option value="V">Veg</option>
                    <option value="N">Non-Veg</option>
                    <option value="E">Egg</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>UOM</label>
                  <select className="form-input" value={form.uom} onChange={e => setField('uom', e.target.value)}>
                    <option value="Pack">Pack</option>
                    <option value="Kilogram">Kilogram</option>
                    <option value="Litre">Litre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>SGST %</label>
                  <input type="number" step="0.01" min="0" className="form-input" value={form.sgst} onChange={e => setField('sgst', e.target.value)} placeholder="SGST %" />
                </div>
                <div className="form-group">
                  <label>CGST %</label>
                  <input type="number" step="0.01" min="0" className="form-input" value={form.cgst} onChange={e => setField('cgst', e.target.value)} placeholder="CGST %" />
                </div>
                <div className="form-group pd-checks">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={form.subsidiary === 'ALL'} onChange={e => setField('subsidiary', e.target.checked ? 'ALL' : sub)} />
                    All Subsidiary
                  </label>
                </div>
                <div className="form-group pd-full pd-checks" style={{ flexDirection: 'row', gap: '2rem' }}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={form.delivery_fee_exempt === 'Y'} onChange={e => setField('delivery_fee_exempt', e.target.checked ? 'Y' : 'N')} />
                    Delivery Fee Exempt
                  </label>
                  {canEditFrozen && (
                    <label className="checkbox-label">
                      <input type="checkbox" checked={form.freeze_definition === 'Y'} onChange={e => setField('freeze_definition', e.target.checked ? 'Y' : 'N')} />
                      Freeze Definition
                    </label>
                  )}
                </div>
                <div className="form-group pd-full">
                  <label>Description</label>
                  <textarea className="form-input form-textarea" rows={2} value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Description" />
                </div>
                <div className="form-group pd-full">
                  <label>Product Image</label>
                  <div className="img-upload-area">
                    {resolveImg(form.image_url) && (
                      <div className="img-preview-wrap">
                        <img src={resolveImg(form.image_url)} alt="preview" className="img-preview" />
                        <button type="button" className="img-clear-btn" onClick={async () => { await deleteServerImage(form.image_url); setField('image_url', ''); }} title="Remove image">×</button>
                      </div>
                    )}
                    <label className={`img-upload-btn${uploading ? ' img-upload-btn--loading' : ''}`}>
                      {uploading ? 'Uploading…' : resolveImg(form.image_url) ? '🔄 Replace Image' : '📷 Upload Image'}
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleImageChange} disabled={uploading} />
                    </label>
                    {form.image_url && (
                      <span className="img-url-hint" title={form.image_url}>{form.image_url}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Kit Settings */}
              <div className="pd-kit-section">
                <div className="pd-kit-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!form.is_kit}
                      onChange={e => setField('is_kit', e.target.checked)}
                    />
                    <strong>This product is a Kit</strong>
                  </label>
                </div>
                {form.is_kit && (
                  <div className="pd-kit-body">
                    <div className="pd-form-grid2" style={{ marginBottom: '12px' }}>
                      <div className="form-group">
                        <label>Item Days *
                          <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                            (total servings — e.g. 20, 24, 28)
                          </span>
                        </label>
                        <input
                          type="number" min="1" className="form-input"
                          value={form.kit_item_days}
                          onChange={e => setField('kit_item_days', e.target.value)}
                          placeholder="e.g. 20"
                          required={form.is_kit}
                        />
                      </div>
                      <div className="form-group">
                        <label>Computed Deliveries</label>
                        <input
                          className="form-input"
                          readOnly
                          value={(() => {
                            const id = Number(form.kit_item_days) || 0;
                            const cp = (form.kit_consumption_days || []).length || 1;
                            const dp = (form.kit_delivery_days    || []).length || 0;
                            if (!id || !dp) return '—';
                            return `${Math.round(id * dp / cp)} deliveries`;
                          })()}
                          style={{ background: '#f5f5f5', color: '#666' }}
                        />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label>Consumption Days
                        <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                          (which days items are used)
                        </span>
                      </label>
                      <div className="pd-kit-days">
                        {DOW_LABELS.map((label, dow) => (
                          <label key={dow} className="pd-kit-day-label">
                            <input
                              type="checkbox"
                              checked={(form.kit_consumption_days || []).includes(dow)}
                              onChange={e => {
                                const days = form.kit_consumption_days || [];
                                setField('kit_consumption_days',
                                  e.target.checked ? [...days, dow].sort() : days.filter(d => d !== dow)
                                );
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label>Delivery Days
                        <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                          (which days physical delivery happens)
                        </span>
                      </label>
                      <div className="pd-kit-days">
                        {DOW_LABELS.map((label, dow) => (
                          <label key={dow} className="pd-kit-day-label">
                            <input
                              type="checkbox"
                              checked={(form.kit_delivery_days || []).includes(dow)}
                              onChange={e => {
                                const days = form.kit_delivery_days || [];
                                setField('kit_delivery_days',
                                  e.target.checked ? [...days, dow].sort() : days.filter(d => d !== dow)
                                );
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Items per Serving Day
                        <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                          (qty for 1 day — system multiplies for multi-day deliveries)
                        </span>
                      </label>
                      <table className="pd-kit-items-table">
                        <thead>
                          <tr><th>#</th><th>Product</th><th>Qty / day</th><th></th></tr>
                        </thead>
                        <tbody>
                          {kitItems.map((item, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>
                                <select
                                  className="form-input"
                                  value={item.item_product_id || ''}
                                  onChange={e => {
                                    const updated = [...kitItems];
                                    const prod = allProducts.find(p => String(p.id) === e.target.value);
                                    updated[idx] = { ...updated[idx], item_product_id: parseInt(e.target.value, 10), item_name: prod?.name || '' };
                                    setKitItems(updated);
                                  }}
                                >
                                  <option value="">— Select Product —</option>
                                  {allProducts.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number" min="1" className="form-input" style={{ width: '70px' }}
                                  value={item.quantity}
                                  onChange={e => {
                                    const updated = [...kitItems];
                                    updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value, 10) || 1 };
                                    setKitItems(updated);
                                  }}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => setKitItems(kitItems.filter((_, i) => i !== idx))}
                                  title="Remove"
                                >✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        type="button"
                        className="btn-cancel"
                        style={{ marginTop: '6px' }}
                        onClick={() => setKitItems([...kitItems, { item_product_id: '', quantity: 1, item_name: '' }])}
                      >+ Add Item</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={saving || uploading}>
                  {saving ? 'Saving…' : modal === 'edit' ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
